"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { usePathname } from "next/navigation";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";

const STORAGE_KEY = "meex_update_processed";
let globalUpdateInProgress = false;

const UpdateNotifier = ({ hasOtherNotifications = false, onActiveChange }) => {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateProgress, setUpdateProgress] = useState("");
  const [isPWA, setIsPWA] = useState(false);

  const pathname = usePathname();
  const updateTimeoutRef = useRef(null);
  const updateCheckRef = useRef(false);
  const waitingWorkerRef = useRef(null);
  const lastCheckTimeRef = useRef(Date.now());
  const backgroundCheckIntervalRef = useRef(null);
  const checkingForUpdateRef = useRef(false);

  const isUpdateProcessed = () => {
    return (
      sessionStorage.getItem(STORAGE_KEY) === "true" || globalUpdateInProgress
    );
  };

  const markUpdateProcessed = () => {
    sessionStorage.setItem(STORAGE_KEY, "true");
    globalUpdateInProgress = true;
  };

  const resetUpdateProcessed = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    globalUpdateInProgress = false;
    updateCheckRef.current = false;
  };

  const checkIfUpdateAvailable = useCallback(async () => {
    if (
      checkingForUpdateRef.current ||
      isUpdateProcessed() ||
      !("serviceWorker" in navigator)
    ) {
      return false;
    }

    checkingForUpdateRef.current = true;

    try {
      const registration = await navigator.serviceWorker.ready;

      if (registration.waiting && !isUpdateProcessed()) {
        waitingWorkerRef.current = registration.waiting;
        if (!hasOtherNotifications) {
          if (updateTimeoutRef.current) {
            clearTimeout(updateTimeoutRef.current);
          }
          updateTimeoutRef.current = setTimeout(() => {
            setOpen(true);
          }, 1500);
        }
        checkingForUpdateRef.current = false;
        return true;
      }

      await registration.update();

      await new Promise((resolve) => setTimeout(resolve, 2000));

      if (registration.waiting && !isUpdateProcessed()) {
        waitingWorkerRef.current = registration.waiting;
        if (!hasOtherNotifications) {
          if (updateTimeoutRef.current) {
            clearTimeout(updateTimeoutRef.current);
          }
          updateTimeoutRef.current = setTimeout(() => {
            setOpen(true);
          }, 1500);
        }
        checkingForUpdateRef.current = false;
        return true;
      }

      checkingForUpdateRef.current = false;
      return false;
    } catch (error) {
      checkingForUpdateRef.current = false;
      return false;
    }
  }, [hasOtherNotifications]);

  const handleAppResume = useCallback(() => {
    resetUpdateProcessed();
    lastCheckTimeRef.current = Date.now();

    setTimeout(() => {
      checkIfUpdateAvailable();
    }, 500);

    setTimeout(() => {
      if (!open && !isUpdating) {
        checkIfUpdateAvailable();
      }
    }, 3000);

    setTimeout(() => {
      if (!open && !isUpdating) {
        checkIfUpdateAvailable();
      }
    }, 6000);
  }, [open, isUpdating, checkIfUpdateAvailable]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem(STORAGE_KEY);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (onActiveChange) {
      onActiveChange(open || isUpdating);
    }
  }, [open, isUpdating, onActiveChange]);

  useEffect(() => {
    setMounted(true);

    const checkPWAMode = () => {
      const isStandalone = window.matchMedia(
        "(display-mode: standalone)",
      ).matches;
      const isIOSStandalone = window.navigator.standalone === true;
      const isInAppBrowser = window.matchMedia(
        "(display-mode: minimal-ui)",
      ).matches;
      return isStandalone || isIOSStandalone || isInAppBrowser;
    };

    const pwaMode = checkPWAMode();
    setIsPWA(pwaMode);
  }, []);

  // Auto-reload when a new service worker takes control (skipWaiting: true)
  useEffect(() => {
    if (!mounted || !isPWA || !("serviceWorker" in navigator)) return;

    let reloading = false;
    const handleControllerChange = () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener(
      "controllerchange",
      handleControllerChange,
    );

    return () => {
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        handleControllerChange,
      );
    };
  }, [mounted, isPWA]);

  useEffect(() => {
    if (!mounted || !isPWA || isUpdateProcessed()) {
      return;
    }

    let isInBackground = false;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        isInBackground = true;
      } else if (isInBackground) {
        isInBackground = false;
        handleAppResume();
      }
    };

    const handleFocus = () => {
      if (isInBackground) {
        isInBackground = false;
        handleAppResume();
      }
    };

    const handleBlur = () => {
      isInBackground = true;
    };

    const handlePageShow = (event) => {
      if (event.persisted || isInBackground) {
        isInBackground = false;
        handleAppResume();
      }
    };

    const startBackgroundCheck = () => {
      if (backgroundCheckIntervalRef.current) {
        clearInterval(backgroundCheckIntervalRef.current);
      }

      backgroundCheckIntervalRef.current = setInterval(() => {
        if (!document.hidden && !isInBackground) {
          const timeSinceLastCheck = Date.now() - lastCheckTimeRef.current;
          if (timeSinceLastCheck > 300000) {
            handleAppResume();
          }
        }
      }, 60000);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("pageshow", handlePageShow);

    startBackgroundCheck();

    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      let touchStartTime = 0;

      const handleTouchStart = () => {
        touchStartTime = Date.now();
      };

      const handleTouchEnd = () => {
        const touchDuration = Date.now() - touchStartTime;
        if (touchDuration < 100 && isInBackground) {
          isInBackground = false;
          handleAppResume();
        }
      };

      const handlePageHide = () => {
        isInBackground = true;
      };

      window.addEventListener("pagehide", handlePageHide);
      document.addEventListener("touchstart", handleTouchStart, {
        passive: true,
      });
      document.addEventListener("touchend", handleTouchEnd, { passive: true });

      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
        window.removeEventListener("focus", handleFocus);
        window.removeEventListener("blur", handleBlur);
        window.removeEventListener("pageshow", handlePageShow);

        window.removeEventListener("pagehide", handlePageHide);
        document.removeEventListener("touchstart", handleTouchStart);
        document.removeEventListener("touchend", handleTouchEnd);

        if (backgroundCheckIntervalRef.current) {
          clearInterval(backgroundCheckIntervalRef.current);
        }
      };
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("pageshow", handlePageShow);

      if (backgroundCheckIntervalRef.current) {
        clearInterval(backgroundCheckIntervalRef.current);
      }
    };
  }, [mounted, isPWA, handleAppResume]);

  useEffect(() => {
    if (
      !mounted ||
      !isPWA ||
      pathname === "/video" ||
      updateCheckRef.current ||
      hasOtherNotifications ||
      (isUpdateProcessed() && !isUpdating)
    ) {
      return;
    }

    if ("serviceWorker" in navigator) {
      const handleUpdateFound = (registration) => {
        const newWorker = registration.installing;

        if (newWorker && !isUpdateProcessed()) {
          waitingWorkerRef.current = newWorker;

          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && !isUpdateProcessed()) {
              if (updateTimeoutRef.current) {
                clearTimeout(updateTimeoutRef.current);
              }

              updateTimeoutRef.current = setTimeout(() => {
                if (!hasOtherNotifications && !isUpdateProcessed()) {
                  setOpen(true);
                }
              }, 1500);
            }
          });
        }
      };

      navigator.serviceWorker.ready.then((registration) => {
        registration.addEventListener("updatefound", () => {
          handleUpdateFound(registration);
        });

        // Check on mount if update is already waiting
        if (registration.waiting && !isUpdateProcessed()) {
          waitingWorkerRef.current = registration.waiting;
          if (!hasOtherNotifications) {
            setOpen(true);
          }
        } else {
          checkIfUpdateAvailable();
        }
      });

      updateCheckRef.current = true;

      return () => {
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }
      };
    }
  }, [
    mounted,
    isPWA,
    pathname,
    hasOtherNotifications,
    isUpdating,
    checkIfUpdateAvailable,
  ]);

  useEffect(() => {
    if (hasOtherNotifications && open && !isUpdating) {
      setOpen(false);
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
    }
  }, [hasOtherNotifications, open, isUpdating]);

  const handleUpdate = async () => {
    if (isUpdateProcessed() && !isUpdating) {
      return;
    }

    setOpen(true);
    setIsUpdating(true);
    markUpdateProcessed();

    try {
      setUpdateProgress("Preparing update...");
      await new Promise((resolve) => setTimeout(resolve, 800));

      if (waitingWorkerRef.current) {
        setUpdateProgress("Installing new version...");
        waitingWorkerRef.current.postMessage({ type: "SKIP_WAITING" });

        await new Promise((resolve) => {
          const handleControllerChange = () => {
            navigator.serviceWorker.removeEventListener(
              "controllerchange",
              handleControllerChange,
            );
            resolve();
          };

          navigator.serviceWorker.addEventListener(
            "controllerchange",
            handleControllerChange,
          );

          setTimeout(resolve, 3000);
        });
      }

      setUpdateProgress("Finishing up...");
      await new Promise((resolve) => setTimeout(resolve, 600));

      setUpdateProgress("Loading updated app...");

      document.body.style.transition = "opacity 0.3s ease-out";
      document.body.style.opacity = "0";

      setTimeout(() => {
        window.location.reload();
      }, 300);
    } catch (error) {
      sessionStorage.removeItem(STORAGE_KEY);
      globalUpdateInProgress = false;

      setUpdateProgress("Update failed, retrying...");

      setTimeout(() => {
        document.body.style.transition = "opacity 0.3s ease-out";
        document.body.style.opacity = "0";

        setTimeout(() => {
          window.location.reload();
        }, 300);
      }, 1000);
    }
  };

  const shouldRender =
    mounted &&
    isPWA &&
    pathname !== "/video" &&
    !hasOtherNotifications &&
    (!isUpdateProcessed() || isUpdating);

  return (
    <>
      {shouldRender && (
        <ConfirmDialog
          open={open || isUpdating}
          onClose={() => {}}
          onConfirm={handleUpdate}
          title="Update available"
          body="A new version of Meex is available with improvements and bug fixes. Please update to continue using the app."
          confirmButtonText={isUpdating ? "Updating" : "Update Now"}
          showIgnoreButton={false}
          showCloseIcon={false}
          isProcessing={isUpdating}
          processingText={updateProgress || "Updating"}
          disableBackdropClick={true}
          disableEscapeKeyDown={true}
          showSnoozeOption={false}
        />
      )}
    </>
  );
};

export default UpdateNotifier;
